import { Head, router, usePage } from "@inertiajs/react"

import { BuildingBeat } from "@/components/agents/setup/building-beat"
import { AppointmentsStep } from "@/components/agents/setup/receptionist/appointments-step"
import { ConnectStep } from "@/components/agents/setup/receptionist/connect-step"
import { MessagesStep } from "@/components/agents/setup/receptionist/messages-step"
import { PlaceholderStep } from "@/components/agents/setup/receptionist/placeholder-step"
import {
  STEP_ORDER,
  stepFor,
} from "@/components/agents/setup/receptionist/steps"
import { TransfersStep } from "@/components/agents/setup/receptionist/transfers-step"
import type {
  CalComEventType,
  CalendlyEventType,
  ConnectedCalendars,
  SetupAgent,
  SetupStepProps,
  SlackSetup,
  TransferPreset,
  VoiceOption,
} from "@/components/agents/setup/types"
import type { DayHours } from "@/components/business-profile/hours-editor"
import AppLayout from "@/layouts/app-layout"
import { setupAgentPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface Props {
  agent: SetupAgent
  business_phone: string | null
  slack: SlackSetup
  connected_calendars: ConnectedCalendars
  calendly_event_types: CalendlyEventType[]
  cal_com_event_types: CalComEventType[]
  transfer_presets: TransferPreset[]
  transfer_hours_prefill: DayHours[]
  voices: VoiceOption[]
}

// IMP-461: the assistant builds the receptionist from the business profile
// first, then the client makes three consequence-framed decisions and goes
// live. The beat shows once (setup untouched) and advances on its own.
const BUILDING_LINES = [
  "Analyzing your business…",
  "Preparing for every kind of call…",
  "Getting ready to answer your calls…",
]

// Maps a persisted step key to the component that should render it. Only
// real steps need an entry here; anything missing falls back to the generic
// PlaceholderStep. Scaffolding a new step is just:
//   1. Add the component file under components/agents/setup/receptionist/
//   2. Register it below
const STEP_COMPONENTS: Partial<
  Record<(typeof STEP_ORDER)[number], React.ComponentType<SetupStepProps>>
> = {
  "step-messages": MessagesStep,
  "step-appointments": AppointmentsStep,
  "step-transfers": TransfersStep,
  "step-connect": ConnectStep,
}

export default function ReceptionistSetup() {
  const {
    agent,
    business_phone,
    slack,
    connected_calendars,
    // The four integration props are server-side deferred (see
    // AgentsController#show + #setup_props). Defaults to [] keep
    // child components happy during the first render, before
    // Inertia's auto-followup populates them.
    calendly_event_types = [],
    cal_com_event_types = [],
    transfer_presets,
    transfer_hours_prefill,
    voices,
  } = usePage().props as unknown as Props

  const breadcrumbs: BreadcrumbItem[] = [
    { title: agent.archetype.name, href: `/agents/${agent.uid}` },
  ]

  // A fresh agent (no step persisted yet) opens with the building beat, which
  // persists the first step when it finishes so a return visit skips it.
  if (!agent.setup_step) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <Head title={`${agent.archetype.name} setup`} />
        <BuildingBeat
          title="Building your receptionist"
          lines={BUILDING_LINES}
          onDone={() =>
            router.patch(
              setupAgentPath({ id: agent.uid }),
              { setup_step: "step-messages" },
              { preserveScroll: true },
            )
          }
        />
      </AppLayout>
    )
  }

  const step = stepFor(agent.setup_step)
  const stepNumber = STEP_ORDER.indexOf(step) + 1
  const totalSteps = STEP_ORDER.length

  const StepComponent = STEP_COMPONENTS[step]
  const sharedProps: SetupStepProps = {
    agent,
    stepNumber,
    totalSteps,
    slack,
    connectedCalendars: connected_calendars,
    calendlyEventTypes: calendly_event_types,
    calComEventTypes: cal_com_event_types,
    transferPresets: transfer_presets,
    transferHoursPrefill: transfer_hours_prefill,
    voices,
    businessPhone: business_phone,
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${agent.archetype.name} setup`} />
      {StepComponent ? (
        <StepComponent {...sharedProps} />
      ) : (
        <PlaceholderStep {...sharedProps} step={step} />
      )}
    </AppLayout>
  )
}
